import { twMerge } from 'tailwind-merge';
import { LoadingSkeleton } from './loading-skeleton';
import { ContextUserType } from '@ui/constants/context';
import { Icon } from './icon';
import Lightningbolt from '@ui/assets/images/Lightningbolt.png';
// import defaultAvatar from '@ui/assets/images/DefaultAvatar.png';
import defaultAvatar from '@ui/assets/images/placeholder_ava.jpg';

const GRADIENT_STYLE: Record<string, string> = {
  fan: 'bg-gradient-to-r from-[#8F00FF] to-[#8F00FF]',
  creator: 'bg-gradient-to-r from-[#8F00FF] to-[#FFA500]',
};

// Theme colors that complement #FF600D (main theme color)
const AVATAR_COLORS = [
  'bg-[#FF600D]',
  'bg-[#FF8F4D]',
  'bg-[#E64C00]',
  'bg-[#FFB38A]',
  'bg-[#FF4500]',
  'bg-[#FF7F50]',
  'bg-[#FF9966]',
  'bg-[#FF5733]',
];

const AVATAR_SIZE = {
  xxl: 'w-44 h-44 text-8xl',
  xl: 'w-32 h-32 text-6xl',
  l: 'h-24 w-24 text-5xl',
  ml: 'h-20 w-20 text-4xl',
  m: 'h-16 w-16 text-3xl',
  sm: 'h-12 w-12 text-2xl',
  s: 'h-8 w-8 text-xl',
  xs: 'h-6 w-6 text-base',
};

const SUBSCRIPTION_ICON_SIZE = {
  xxl: 'h-10 w-10 bottom-[-20px]',
  xl: 'h-8 w-8 bottom-[-18px]',
  l: 'h-7 w-7 bottom-[-16px]',
  ml: 'h-6 w-6 bottom-[-14px]',
  m: 'h-5 w-5 bottom-[-12px]',
  sm: 'h-4 w-4 bottom-[-10px]',
  s: 'h-3 w-3 bottom-[-8px]',
  xs: 'h-2.5 w-2.5 bottom-[-6px]',
};

const GLOW_BORDER_SIZE = {
  xxl: 'border-[6px]',
  xl: 'border-[5px]',
  l: 'border-[4px]',
  ml: 'border-[3px]',
  m: 'border-[3px]',
  sm: 'border-[2px]',
  s: 'border-[2px]',
  xs: 'border-[1px]',
};

const BORDER_SIZE = {
  xxl: 'border-4',
  xl: 'border-4',
  l: 'border-4',
  ml: 'border-2',
  m: 'border-2',
  sm: 'border-2',
  s: 'border-2',
  xs: 'border-2',
};

const GRADIENT_SIZE = {
  xxl: 'p-1',
  xl: 'p-1',
  l: 'p-1',
  ml: 'p-1',
  m: 'p-1',
  sm: 'p-0.5',
  s: 'p-0.5',
  xs: 'p-0.5',
};

export type IAvatarSize = keyof typeof AVATAR_SIZE;

export interface IAvatar {
  src?: string;
  displayName?: string;
  size?: IAvatarSize;
  type?: ContextUserType;
  className?: string;
  wrapperClassName?: string;
  withGradient?: boolean;
  isLoading?: boolean;
  isSubscribed?: boolean;
}

// Function to get consistent but random color based on the user name
function getAvatarColor(name: string): string {
  // Convert name to a simple hash for color selection
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Use the hash to pick a color from our palette
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
}

// Function to get the first letter or undefined when no name is available
function getInitial(displayName?: string): string | undefined {
  if (displayName && displayName.length > 0) {
    return displayName[0].toUpperCase();
  }

  return undefined;
}

export function Avatar({
  size,
  src,
  displayName,
  className,
  isLoading,
  withGradient,
  wrapperClassName,
  type,
  isSubscribed,
}: IAvatar) {
  const key = size || 'm';
  const sizeStyle = AVATAR_SIZE[key];
  const borderStyle = BORDER_SIZE[key];
  const gradientSize = GRADIENT_SIZE[key];
  const iconSize = SUBSCRIPTION_ICON_SIZE[key];
  const glowBorderSize = GLOW_BORDER_SIZE[key];

  const initial = getInitial(displayName);
  const nameForColor = displayName || '';
  const avatarColor = initial ? getAvatarColor(nameForColor) : '';

  const gradientStyle = isSubscribed
    ? 'bg-gradient-to-br from-[#FB9151] to-[#FFC4A3]' // Second orange is lighter
    : GRADIENT_STYLE[type || ''];

  const glowStyle = isSubscribed
    ? twMerge(
        'border-transparent bg-[linear-gradient(45deg, white 15%, #FB9151 50%, #FB9151 100%)]',
        glowBorderSize,
      )
    : '';

  return (
    <div
      className={twMerge(
        'aspect-square h-max w-max rounded-full bg-transparent bg-gradient-to-br from-[#8F00FF] to-[#FFA500]',
        gradientSize,
        withGradient && !isLoading ? '' : 'from-transparent to-transparent',
        wrapperClassName,
        gradientStyle,
        glowStyle,
      )}>
      {isLoading ? (
        <LoadingSkeleton
          className={twMerge(
            'aspect-square rounded-full border-white',
            sizeStyle,
            borderStyle,
            className,
          )}
        />
      ) : (
        <div className="relative">
          {src ? (
            <img
              className={twMerge(
                'aspect-square rounded-full border-white bg-white object-cover',
                sizeStyle,
                borderStyle,
                className,
              )}
              src={src}
              alt={initial ? `${initial}'s avatar` : 'User avatar'}
            />
          ) : initial ? (
            <div
              className={twMerge(
                'aspect-square rounded-full border-white flex items-center justify-center text-white font-bold',
                sizeStyle,
                borderStyle,
                className,
                avatarColor,
              )}>
              {initial}
            </div>
          ) : (
            <img
              className={twMerge(
                'aspect-square rounded-full border-white bg-white object-cover',
                sizeStyle,
                borderStyle,
                className,
              )}
              src={defaultAvatar.src}
              alt="Default avatar"
            />
          )}

          {isSubscribed && (
            <img
              src={Lightningbolt.src}
              className={twMerge(
                'absolute left-1/2 transform -translate-x-1/2',
                iconSize, // Dynamic size & position
              )}
              alt="Subscribed"
            />
          )}
        </div>
      )}
    </div>
  );
}
